import { apiSlice } from './apiSlice';

/**
 * Trademate — the trade journal and the Twitter feed's profile list. Everything
 * else on the page is a third-party widget and talks to its own host.
 *
 *   GET  /v2/trademate/trading-journal/all/ → { journal: [entry] }
 *   GET  /v2/get/twitter-profiles/          → { success, data: [screenName] }
 *   POST /v2/update/twitter-profiles/       { profiles: [screenName] } → same
 *
 * A journal entry carries its whole plan, so the list is all the page needs —
 * live's per-entry `details/:id/` call returns the same object again. Fields
 * the page reads: `date` (ISO), `symbol`, `trade_type` (Buy/Sell), `entry`
 * (In/Out), `profit`, `size`, `counter_trend` (bool or null), `trigger`,
 * `supporting_evidence`, `opposing_evidence`, `heads_up`, `notes` (HTML from
 * the old rich-text editor), `order_price`, `max_stop_price`, `max_loss`,
 * `technical_stop`, `exit_traget` (sic), `time_stop`, `risk_or_reward`.
 */
export const trademateApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getTradingJournal: builder.query({
      query: () => '/v2/trademate/trading-journal/all/',
      providesTags: ['Journal'],
    }),
    /**
     * POST /v2/trademate/trading-journal/create/ — one entry, in the same
     * fields the list returns (see the note above). The Trademate journal
     * refetches, so a trade added from Trade History is there when it's opened.
     */
    createTradingJournal: builder.mutation({
      query: (body) => ({
        url: '/v2/trademate/trading-journal/create/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Journal'],
    }),
    getTwitterProfiles: builder.query({
      query: () => '/v2/get/twitter-profiles/',
    }),
    updateTwitterProfiles: builder.mutation({
      query: (profiles) => ({
        url: '/v2/update/twitter-profiles/',
        method: 'POST',
        body: { profiles },
      }),
      // The response is the saved list, so the feed redraws from it directly.
      async onQueryStarted(profiles, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        dispatch(
          trademateApi.util.updateQueryData('getTwitterProfiles', undefined, (draft) => {
            draft.data = data?.data ?? profiles;
          }),
        );
      },
    }),
  }),
});

export const {
  useGetTradingJournalQuery,
  useCreateTradingJournalMutation,
  useGetTwitterProfilesQuery,
  useUpdateTwitterProfilesMutation,
} = trademateApi;
